import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import api from "@/api";
import ProviderCard from "@/components/ProviderCard";
import Seo from "@/components/Seo";

// Maps URL slug → display + filter
const COUNTRY_MAP = {
  "united-states": "United States", "usa": "United States",
  "united-kingdom": "United Kingdom", "germany": "Germany",
  "netherlands": "Netherlands", "singapore": "Singapore", "france": "France",
  "switzerland": "Switzerland", "canada": "Canada", "australia": "Australia",
  "india": "India", "china": "China", "japan": "Japan", "israel": "Israel",
};

const CATEGORY_MAP = {
  "cold-chain-monitoring": "Cold chain monitoring",
  "asset-tracking": "Asset tracking",
  "shipment-tracking": "Shipment tracking",
  "real-time-location-tracking": "Real-time location tracking",
  "returnable-packaging-tracking": "Returnable packaging tracking",
  "fleet-tracking": "Fleet tracking",
  "container-tracking": "Container tracking",
  "pallet-tracking": "Pallet tracking",
  "indoor-positioning": "Indoor positioning",
  "ble-location-tracking": "BLE location tracking",
  "pharma-logistics": "Pharma logistics",
  "food-logistics": "Food logistics",
};

const INDUSTRY_MAP = {
  "pharma": "Life sciences and pharma",
  "life-sciences": "Life sciences and pharma",
  "logistics": "Logistics",
  "food-and-beverage": "Food and beverage",
  "manufacturing": "Manufacturing",
  "automotive": "Automotive",
  "cold-chain": "Cold chain",
};

export default function SeoLanding({ type }) {
  const params = useParams();
  const segment = params.country || params.category || params.industry || params.useCase || params.deviceType || params.sensor;
  const [provs, setProvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [filterDisplay, setFilterDisplay] = useState("");

  useEffect(() => {
    if (!segment && !type) return;
    let url = "/providers?page=1&page_size=24";
    let pageTitle = "";
    let display = "";

    if (type === "country" && segment) {
      const country = COUNTRY_MAP[segment] || segment.replace(/-/g, " ");
      url += `&country=${encodeURIComponent(country)}`;
      pageTitle = `Tracking Solution Providers in ${country}`;
      display = country;
    } else if (type === "category" && segment) {
      const cat = CATEGORY_MAP[segment] || segment.replace(/-/g, " ");
      url += `&category=${encodeURIComponent(cat)}`;
      pageTitle = `Best ${cat} Providers`;
      display = cat;
    } else if (type === "industry" && segment) {
      const ind = INDUSTRY_MAP[segment] || segment.replace(/-/g, " ");
      url += `&industry=${encodeURIComponent(ind)}`;
      pageTitle = `Tracking Solutions for ${ind}`;
      display = ind;
    } else if (type === "static") {
      // Static landing pages
      const map = {
        "returnable-packaging-tracking": { cat: "Returnable packaging tracking", title: "Returnable Packaging Tracking Providers" },
        "smart-label-tracking": { device: "Smart label", title: "Smart Label Tracking Providers" },
        "ble-asset-tracking": { cat: "BLE location tracking", title: "BLE Asset Tracking Providers" },
        "satellite-tracking-devices": { device: "Satellite tracker", title: "Satellite Tracking Devices" },
        "pharma-tracking-solutions": { ind: "Life sciences and pharma", title: "Pharma Tracking Solutions" },
        "food-logistics-tracking-solutions": { ind: "Food and beverage", title: "Food Logistics Tracking Solutions" },
      };
      const cfg = map[segment] || {};
      if (cfg.cat) url += `&category=${encodeURIComponent(cfg.cat)}`;
      if (cfg.device) url += `&device_type=${encodeURIComponent(cfg.device)}`;
      if (cfg.ind) url += `&industry=${encodeURIComponent(cfg.ind)}`;
      pageTitle = cfg.title || "Tracking Solutions";
      display = pageTitle;
    }

    setTitle(pageTitle);
    setFilterDisplay(display);
    document.title = `${pageTitle} | Strategic Tracking`;
    setLoading(true);
    api.get(url).then(({ data }) => setProvs(data.items || [])).finally(() => setLoading(false));
  }, [segment, type]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo
        title={title}
        description={`Compare verified tracking providers ${filterDisplay ? `covering ${filterDisplay.toLowerCase()}` : ""} on Strategic Tracking — the specialist global directory for shipment visibility, condition monitoring and asset tracking technology.`}
      />
      <div className="max-w-3xl">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Directory landing</div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 mt-2 tracking-tighter">{title}</h1>
        <p className="mt-5 text-lg text-slate-600">
          Compare verified tracking providers covering {filterDisplay.toLowerCase()} on Strategic Tracking — the
          specialist global directory for shipment visibility, condition monitoring and asset tracking
          technology.
        </p>
        <div className="mt-6 flex gap-3 flex-wrap">
          <Link to="/solutions" className="btn-primary"><Search size={16}/> Search all solutions</Link>
          <Link to="/request-quotes" className="btn-secondary">Request recommendations <ArrowRight size={14}/></Link>
        </div>
      </div>

      <div className="mt-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{provs.length} providers</div>
        </div>
        {loading ? <div className="text-slate-500">Loading…</div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {provs.map((p) => (
              <ProviderCard key={p.id} provider={p} onCompare={() => {}}/>
            ))}
            {provs.length === 0 && (
              <div className="col-span-full card-elevated p-12 text-center">
                <div className="font-display font-bold text-lg">No providers indexed yet</div>
                <p className="mt-2 text-sm text-slate-500">Try the full directory or submit a missing provider.</p>
                <Link to="/solutions" className="btn-primary mt-4 inline-flex">Browse all</Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* FAQ-style content */}
      <section className="mt-16 grid md:grid-cols-3 gap-6">
        <div className="card-elevated p-6">
          <div className="font-display font-bold">What to look for</div>
          <p className="mt-2 text-sm text-slate-600">Connectivity, sensor capabilities, battery life and commercial model determine fit for your use case.</p>
        </div>
        <div className="card-elevated p-6">
          <div className="font-display font-bold">How quotes are routed</div>
          <p className="mt-2 text-sm text-slate-600">Verified providers receive your enquiry directly. Basic listings are handled by our team.</p>
        </div>
        <div className="card-elevated p-6">
          <div className="font-display font-bold">Need help shortlisting?</div>
          <p className="mt-2 text-sm text-slate-600">Submit a multi-provider quote request and we'll match you with vetted vendors.</p>
        </div>
      </section>
    </div>
  );
}
